import { Stack, Text } from '@mantine/core';
import type { FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { RadioField, type RadioFieldProps } from './RadioField';

export type SignaturesRadioFieldProps<T extends FieldValues> =
  RadioFieldProps<T> & {
    disabledSignatures: ('SMS' | 'QUALIFIED' | 'HYBRID' | 'ANY')[];
  };

const ExpandedLabel: React.FC<{ type: string }> = ({ type }) => {
  const { t } = useTranslation('common');
  return (
    <Stack spacing={0}>
      <Text fz="lg" fw={600}>
        {t(`signTypes.${type}`)}
      </Text>
      <Text fz="sm" fw={400}>
        {t(`signTypes.${type}Description`)}
      </Text>
    </Stack>
  );
};

/**
 * SignaturesRadioField component
 *
 * @example
 * ```tsx
 * <SignaturesRadioField name="field.name" />
 * ```
 *
 * @public
 * @param label - label
 * @param props - other props
 * @param disabledSignatures - disabled signatures
 */
export function SignaturesRadioField<T extends FieldValues>({
  label,
  disabledSignatures = [],
  ...props
}: SignaturesRadioFieldProps<T>) {
  return (
    <Stack spacing={8}>
      {label && (
        <Text fz="lg" fw={500}>
          {label}
        </Text>
      )}
      <RadioField
        value="SMS"
        label={<ExpandedLabel type="sms" />}
        disabled={disabledSignatures.includes('SMS')}
        {...props}
      />
      <RadioField
        value="QUALIFIED"
        label={<ExpandedLabel type="qualified" />}
        disabled={disabledSignatures.includes('QUALIFIED')}
        {...props}
      />
      <RadioField
        value="HYBRID"
        label={<ExpandedLabel type="hybrid" />}
        disabled={disabledSignatures.includes('HYBRID')}
        {...props}
      />
      <RadioField
        value="ANY"
        label={<ExpandedLabel type="any" />}
        disabled={disabledSignatures.includes('ANY')}
        {...props}
      />
    </Stack>
  );
}
